'use client'

/**
 * Purpose: Survey form state machine — pages, validation, photo vs questionnaire modes.
 * Inputs: locale, optional prefillProfile; feature flag for photo path.
 * Outputs: Page state, handlers, and progress labels consumed by SurveyForm.
 */

import { useState, useEffect, useTransition } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  captureSurveyStarted,
  captureSurveySubmitFailed,
  captureSurveyCompleted,
} from '@lib/interaction-telemetry'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { submitSurvey } from '@/actions/survey'
import {
  OILY_DRY_QUESTIONS,
  SENSITIVE_RESISTANT_QUESTIONS,
  PIGMENTED_QUESTIONS,
  WRINKLED_QUESTIONS,
} from '@lib/survey-questions'
import type { FormState, ActionError } from '@lib/models/types'
import type { AxisKey } from '@lib/survey-constants'
import type { FaceAnalysisResponse } from '@lib/models/types'
import {
  getAxisPage,
  getAxisQuestions,
  getAxisFromPage,
  getFormStateAnswers,
  withAxisPrefillAnswers,
  withSectionAnswer,
  withSkinKnowledge,
  withSliderValue,
  getPrefilledAnswer,
} from '@lib/survey-axis-maps'

// Pages: 0=welcome, 1=clientInfo, 2=skinKnowledge, 3=oilyDry, 4=sensitiveResistant, 5=pigmented, 6=wrinkled, 7=consent
export const TOTAL_PAGES = 8
export const CONSENT_PAGE = 7

// Default empty survey state — prefillProfile only seeds name/email.
const INITIAL: FormState = {
  name: '',
  email: '',
  age: '',
  gender: '',
  oilyDryAnswers: {},
  sensitiveResistantAnswers: {},
  pigmentedAnswers: {},
  wrinkledAnswers: {},
  consentGiven: false,
  skinKnowledge: { oilyDry: null, sensitiveResistant: null, pigmented: null, wrinkled: null },
  sliderValues: { oilyDry: 50, sensitiveResistant: 50, pigmented: 50, wrinkled: 50 },
}

export type SurveyPrefillProfile = { name: string; email: string }

// Logged-in onboarding passes name/email so page 1 can skip those fields.
function buildInitialFormState(prefillProfile?: SurveyPrefillProfile): FormState {
  if (!prefillProfile) return INITIAL
  return { ...INITIAL, name: prefillProfile.name, email: prefillProfile.email }
}

export function useSurveyForm({
  locale,
  prefillProfile,
}: {
  locale: string
  prefillProfile?: SurveyPrefillProfile
}) {
  // Router + search params drive post-submit redirect and onboarding telemetry.
  const router = useRouter()
  const searchParams = useSearchParams()
  const fromOnboarding = searchParams.get('fromOnboarding') === '1'
  const t = useTranslations('survey')
  const [page, setPage] = useState(0)
  const [formState, setFormState] = useState<FormState>(() => buildInitialFormState(prefillProfile))
  const [pageError, setPageError] = useState('')
  const [actionError, setActionError] = useState<ActionError | null>(null)
  // isPending wraps the server-action submit on the consent page.
  const [isPending, startTransition] = useTransition()

  // Questionnaire vs photo path — photo state clears when user switches back.
  const [mode, setMode] = useState<'survey' | 'photo'>('survey')
  const [analyzed, setAnalyzed] = useState(false)
  const [photoRatings, setPhotoRatings] = useState<Record<string, number> | null>(null)
  const [lowConfidenceAxes, setLowConfidenceAxes] = useState<AxisKey[]>([])
  const [faceImageKey, setFaceImageKey] = useState<string | null>(null)
  const [biometricConsentTimestamp, setBiometricConsentTimestamp] = useState('')

  // image-survey flag gates the welcome-page photo CTA.
  const photoEnabled = useFeatureFlag('image-survey')

  // Photo mode skips axes Claude scored confidently; always ends on consent page.
  const photoPages = [
    ...lowConfidenceAxes.map((a) => getAxisPage(a)).sort((a, b) => a - b),
    CONSENT_PAGE,
  ]

  // Merge face-analysis ratings into form state and jump to first refine page.
  function handleAnalyzed(result: FaceAnalysisResponse, consentTimestamp: string) {
    const lowAxes = (result.lowConfidenceAxes ?? []) as AxisKey[]
    setPhotoRatings(result.ratings ?? null)
    setLowConfidenceAxes(lowAxes)
    setFaceImageKey(result.imageKey ?? null)
    setBiometricConsentTimestamp(consentTimestamp)

    // Seed questionnaire answers for axes that need manual refinement.
    setFormState((prev) => {
      let next: FormState = { ...prev, consentGiven: true }
      if (result.prefilledAnswers) {
        for (const axis of lowAxes) {
          const seed = getPrefilledAnswer(result.prefilledAnswers, axis)
          if (seed) next = withAxisPrefillAnswers(next, axis, seed)
        }
      }
      return next
    })

    setAnalyzed(true)
    const firstPage = lowAxes.length > 0 ? Math.min(...lowAxes.map((a) => getAxisPage(a))) : CONSENT_PAGE
    setPage(firstPage)
  }

  // Abandon photo path — reset analysis state and start questionnaire at client info.
  function switchToQuestionnaire() {
    setMode('survey')
    setAnalyzed(false)
    setPhotoRatings(null)
    setLowConfidenceAxes([])
    setFaceImageKey(null)
    setPageError('')
    setPage(1)
  }

  // Thin wrappers around pure helpers in survey-axis-maps.
  function updateSection(
    section: keyof Pick<
      FormState,
      'oilyDryAnswers' | 'sensitiveResistantAnswers' | 'pigmentedAnswers' | 'wrinkledAnswers'
    >,
    key: string,
    value: string,
  ) {
    setFormState((prev) => withSectionAnswer(prev, section, key, value))
  }

  function setKnowledge(axis: keyof FormState['skinKnowledge'], value: boolean) {
    setFormState((prev) => withSkinKnowledge(prev, axis, value))
  }

  function setSlider(axis: keyof FormState['sliderValues'], value: number) {
    setFormState((prev) => withSliderValue(prev, axis, value))
  }

  // Per-page required-field checks differ for photo-refine vs full questionnaire.
  function validateCurrentPage(): boolean {
    // Photo path after analysis: only validate low-confidence axis pages + consent.
    if (mode === 'photo' && analyzed) {
      const axis = getAxisFromPage(page)
      if (axis) {
        const questions = getAxisQuestions(axis)
        const answers = getFormStateAnswers(formState, axis)
        if (questions.find((q) => !answers[q.key])) {
          setPageError(t('requiredQuestionError'))
          return false
        }
        return true
      }
      if (page === CONSENT_PAGE && !formState.consentGiven) {
        setPageError('You must give consent to submit the survey.')
        return false
      }
      return true
    }
    if (page === 1) {
      if (!prefillProfile) {
        if (!formState.name.trim()) {
          setPageError('Please enter your name.')
          return false
        }
        // Basic email shape check before server-side validation on submit.
        if (!formState.email.trim()) {
          setPageError('Please enter your email.')
          return false
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formState.email.trim())) {
          setPageError('Please enter a valid email address.')
          return false
        }
      }
    }
    if (page === 2) {
      const { oilyDry, sensitiveResistant, pigmented, wrinkled } = formState.skinKnowledge
      // All four self-knowledge toggles must be answered before axis pages.
      if (oilyDry === null || sensitiveResistant === null || pigmented === null || wrinkled === null) {
        setPageError(t('requiredQuestionError'))
        return false
      }
    }
    if (page === 3 && formState.skinKnowledge.oilyDry === false) {
      const missing = OILY_DRY_QUESTIONS.find((q) => !formState.oilyDryAnswers[q.key])
      if (missing) {
        setPageError(t('requiredQuestionError'))
        return false
      }
    }
    // Sensitive axis — only when user said they do not know their sensitivity.
    if (page === 4 && formState.skinKnowledge.sensitiveResistant === false) {
      const missing = SENSITIVE_RESISTANT_QUESTIONS.find((q) => !formState.sensitiveResistantAnswers[q.key])
      if (missing) {
        setPageError(t('requiredQuestionError'))
        return false
      }
    }
    if (page === 5 && formState.skinKnowledge.pigmented === false) {
      const missing = PIGMENTED_QUESTIONS.find((q) => !formState.pigmentedAnswers[q.key])
      if (missing) {
        setPageError(t('requiredQuestionError'))
        return false
      }
    }
    // Wrinkled axis — only when user said they do not know their wrinkle tendency.
    if (page === 6 && formState.skinKnowledge.wrinkled === false) {
      const missing = WRINKLED_QUESTIONS.find((q) => !formState.wrinkledAnswers[q.key])
      if (missing) {
        setPageError(t('requiredQuestionError'))
        return false
      }
    }
    if (page === 7 && !formState.consentGiven) {
      setPageError('You must give consent to submit the survey.')
      return false
    }
    return true
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [page])

  function handleNext() {
    setPageError('')
    if (!validateCurrentPage()) return
    if (page === 0) captureSurveyStarted(fromOnboarding)
    // Photo path: jump through low-confidence axis pages then consent.
    if (mode === 'photo' && analyzed) {
      const idx = photoPages.indexOf(page)
      const nextPage = photoPages.at(idx + 1)
      if (idx >= 0 && nextPage !== undefined) setPage(nextPage)
      return
    }
    setPage((p) => Math.min(p + 1, TOTAL_PAGES - 1))
  }

  function handleBack() {
    setPageError('')
    if (mode === 'photo' && analyzed) {
      const idx = photoPages.indexOf(page)
      // First photo-refine page → return to camera capture.
      if (idx <= 0) {
        setAnalyzed(false)
        return
      }
      const prevPage = photoPages.at(idx - 1)
      if (prevPage !== undefined) setPage(prevPage)
      return
    }
    // Questionnaire path walks pages linearly from welcome through consent.
    setPage((p) => Math.max(p - 1, 0))
  }

  // Build FormData for submitSurvey — shape differs for photo vs questionnaire mode.
  function handleSubmit() {
    setPageError('')
    if (!validateCurrentPage()) return

    const fd = new FormData()
    // Demographics and axis answers are always sent — shape differs by mode below.
    fd.set('name', formState.name)
    fd.set('email', formState.email)
    if (formState.age) fd.set('age', formState.age)
    if (formState.gender) fd.set('gender', formState.gender)
    fd.set('oilyDryAnswers', JSON.stringify(formState.oilyDryAnswers))
    fd.set('sensitiveResistantAnswers', JSON.stringify(formState.sensitiveResistantAnswers))
    fd.set('pigmentedAnswers', JSON.stringify(formState.pigmentedAnswers))
    fd.set('wrinkledAnswers', JSON.stringify(formState.wrinkledAnswers))
    // Questionnaire mode sends self-knowledge toggles and slider positions.
    if (mode === 'survey') {
      fd.set('skinKnowledge', JSON.stringify(formState.skinKnowledge))
      fd.set('sliderValues', JSON.stringify(formState.sliderValues))
    }
    fd.set('consentGiven', 'true')
    fd.set('locale', locale)
    // Photo submission carries analysis metadata instead of slider/knowledge answers.
    if (mode === 'photo' && photoRatings) {
      fd.set('analysisSource', 'photo')
      fd.set('photoRatings', JSON.stringify(photoRatings))
      fd.set('lowConfidenceAxes', JSON.stringify(lowConfidenceAxes))
      if (faceImageKey) fd.set('faceImageKey', faceImageKey)
      fd.set('biometricConsentGiven', 'true')
      if (biometricConsentTimestamp) fd.set('biometricConsentTimestamp', biometricConsentTimestamp)
    }
    if (fromOnboarding) {
      fd.set('fromOnboarding', 'true')
    }
    // Scan page sends users here with ?returnTo=scan; the action validates it against a whitelist.
    const returnTo = searchParams.get('returnTo')
    if (returnTo) fd.set('returnTo', returnTo)

    startTransition(async () => {
      const result = await submitSurvey(null, fd)
      // Server action returns error shapes — map to UI and telemetry.
      if (result && 'error' in result) {
        captureSurveySubmitFailed('error', fromOnboarding)
        setActionError(result as ActionError)
      } else if (result && 'errors' in result) {
        captureSurveySubmitFailed(
          'validation',
          fromOnboarding,
          Object.keys((result as ActionError).errors?.fieldErrors ?? {}),
        )
        setActionError(result as ActionError)
      // Success — server action returns the post-survey redirect target.
      } else if (result && 'redirectTo' in result) {
        captureSurveyCompleted({
          fromOnboarding,
          age: formState.age,
          gender: formState.gender,
        })
        // Refresh server layout cache before navigating to the post-survey destination.
        router.refresh()
        router.push(result.redirectTo)
      }
    })
  }

  const inPhotoCapture = mode === 'photo' && !analyzed
  const photoTotal = photoPages.length
  const photoIdx = photoPages.indexOf(page)
  // Progress label and bar width — photo refine uses a shorter page list.
  const stepLabel =
    mode === 'photo' && analyzed
      ? `Step ${photoIdx + 1} of ${photoTotal}`
      : `Step ${page + 1} of ${TOTAL_PAGES}`
  const progress =
    mode === 'photo' && analyzed
      ? Math.round(((photoIdx + 1) / photoTotal) * 100)
      : Math.round((page / (TOTAL_PAGES - 1)) * 100)

  // Public API consumed by SurveyForm and page components.
  return {
    t,
    page,
    formState,
    setFormState,
    pageError,
    actionError,
    isPending,
    mode,
    analyzed,
    photoEnabled,
    inPhotoCapture,
    stepLabel,
    progress,
    handleAnalyzed,
    switchToQuestionnaire,
    updateSection,
    setKnowledge,
    setSlider,
    handleNext,
    handleBack,
    handleSubmit,
    setMode,
  }
}
